import { useState } from "react";

import {
  useGetUserTransmissionProgressAnalyticsQuery,
  useGetTransmissionProgressAnalyticsUsersQuery,
} from "app/store/userActivityLog/api/userActivityLogApi";
import { getFormattedDate, subtractFromDate } from "utils/date";

const customTooltip = {
  enabled: true,
  backgroundColor: "#ffffff",
  titleColor: "#111111",
  bodyColor: "#333333",
  borderColor: "#dddddd",
  borderWidth: 1,
  caretSize: 0,
  cornerRadius: 0,
  padding: 14,
  boxPadding: 8,
  bodySpacing: 6,
  titleSpacing: 6,
  titleMarginBottom: 10,
  usePointStyle: true,
  titleFont: {
    size: 16,
    weight: "bold",
  },
  bodyFont: {
    size: 14,
  },
};

const useUserTransmissionProgressAnalytic = () => {
  const [selectedUserIds, setSelectedUserIds] = useState(["all"]);
  const [startDate, setStartDate] = useState(
    getFormattedDate(subtractFromDate(new Date(), 30).toDate())
  );
  const [endDate, setEndDate] = useState(getFormattedDate(new Date()));

  const {
    data: users = [],
    isError: isFetchUsersError,
    error: fetchUsersError,
  } = useGetTransmissionProgressAnalyticsUsersQuery();
  const { data, isFetching, isError, error } =
    useGetUserTransmissionProgressAnalyticsQuery({
      users: selectedUserIds?.filter((item) => item !== "all"),
      startDate,
      endDate,
    });

  const chartOptions = {
    plugins: {
      tooltip: customTooltip,
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 5,
          boxHeight: 5,
          usePointStyle: true,
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "No of transmissions processed",
        },
      },
    },
  };

  const prepareChartData = () => {
    const labels = !isError ? data?.labels : [];

    const chartData = {
      labels,
      datasets: [
        {
          label: "Archived",
          data: labels?.map(
            (label) =>
              data?.records
                ?.find((item) => item._id === label)
                ?.processed_data?.find(
                  (item) => item.transmission_status === "Archived"
                )?.count
          ),
          backgroundColor: "#BDBDBD",
          barThickness: 25,
        },
        {
          label: "Billable",
          data: labels?.map(
            (label) =>
              data?.records
                ?.find((item) => item._id === label)
                ?.processed_data?.find(
                  (item) => item.transmission_status === "Billable"
                )?.count
          ),
          backgroundColor: "#94BA33",
          barThickness: 25,
        },
        {
          label: "Alert",
          data: labels?.map(
            (label) =>
              data?.records
                ?.find((item) => item._id === label)
                ?.processed_data?.find(
                  (item) => item.transmission_status === "Alert"
                )?.count
          ),
          backgroundColor: "#ef5350",
          barThickness: 25,
        },
      ],
    };

    return chartData;
  };

  const handleStartDateChange = (date) => {
    date ? setStartDate(getFormattedDate(date)) : setStartDate(null);
  };

  const handleEndDateChange = (date) => {
    date ? setEndDate(getFormattedDate(date)) : setEndDate(null);
  };

  return {
    selectedUserIds,
    setSelectedUserIds,
    startDate,
    endDate,
    users,
    data,
    isFetching,
    handleStartDateChange,
    handleEndDateChange,
    chartOptions,
    prepareChartData,
    error,
    isError,
    isFetchUsersError,
    fetchUsersError,
  };
};

export default useUserTransmissionProgressAnalytic;
